/**
 * Test Attempt Date Service
 * 
 * DEPRECATED: MongoDB endpoint removed - now uses API-only architecture
 * Attempt dates are now included in the main API response from myimsApiService
 * This service is kept for backward compatibility but returns empty data
 * 
 * TODO: Refactor components to get attempt dates from API response directly
 */

// Import student ID utility for consistent handling
import StudentIdUtils from './studentIdUtils';

class TestAttemptDateService {
  constructor() {
    this.attemptDateCache = new Map();
    this.loading = false;
  }

  /**
   * Get all test attempt dates for a student
   * DEPRECATED: Returns empty array - use API response instead
   * @param {string} studentPin - Student IMS PIN
   * @returns {Array} Empty array (data now comes from API)
   */
  async getStudentTestAttemptDates(studentPin) {
    console.log(`📅 TestAttemptDate: DEPRECATED - Attempt dates now come from API response`);
    return [];
  }

  /**
   * Get attempt date for a specific test
   * DEPRECATED: Returns null - use API response instead
   * @param {string} studentPin - Student IMS PIN
   * @param {string} testId - Test ID
   * @returns {null} Null (data now comes from API)
   */
  async getTestAttemptDate(studentPin, testId) {
    console.log(`📅 TestAttemptDate: DEPRECATED - Attempt dates now come from API response`);
    return null;
  }

  /**
   * Get attempt dates for multiple tests with enhanced metadata
   * @param {string} studentPin - Student IMS PIN
   * @param {Array} testIds - Array of test IDs
   * @returns {Array} Array of attempt date objects with metadata
   */
  async getMultipleTestAttemptDates(studentPin, testIds) {
    const allAttemptDates = await this.getStudentTestAttemptDates(studentPin);
    
    return testIds.map(testId => {
      const attemptData = allAttemptDates.find(date => date.testId === testId);
      return {
        testId,
        ...attemptData,
        hasAttemptDate: !!attemptData?.attemptDate
      };
    });
  }

  /**
   * Calculate time gap between two tests
   * @param {string} earlierDate - Earlier test date (YYYY-MM-DD)
   * @param {string} laterDate - Later test date (YYYY-MM-DD)
   * @returns {Object} Time gap information
   */
  calculateTimeGap(earlierDate, laterDate) {
    if (!earlierDate || !laterDate) {
      return { days: null, weeks: null, isValid: false };
    }

    try {
      const earlier = new Date(earlierDate);
      const later = new Date(laterDate);
      
      if (isNaN(earlier.getTime()) || isNaN(later.getTime())) {
        return { days: null, weeks: null, isValid: false };
      }

      const timeDiff = later.getTime() - earlier.getTime();
      const daysDiff = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
      
      return {
        days: daysDiff,
        weeks: Math.floor(daysDiff / 7),
        isValid: true,
        isConsistent: daysDiff >= 7 && daysDiff <= 21 // Typical gap between SIMCATs
      };
    } catch (error) {
      console.error('Error calculating time gap:', error);
      return { days: null, weeks: null, isValid: false };
    }
  }

  /**
   * Analyze test attempt pattern for a student
   * @param {string} studentPin - Student IMS PIN
   * @param {Array} availableTests - Available tests with test names
   * @returns {Object} Pattern analysis
   */
  async analyzeAttemptPattern(studentPin, availableTests) {
    const attemptDates = await this.getStudentTestAttemptDates(studentPin);
    
    if (attemptDates.length < 2) {
      return {
        totalTests: attemptDates.length,
        pattern: 'insufficient_data',
        consistency: 'unknown',
        averageGap: null,
        recommendations: []
      };
    }

    // Sort by attempt date
    const sortedDates = attemptDates
      .filter(date => date.attemptDate)
      .sort((a, b) => new Date(a.attemptDate) - new Date(b.attemptDate));

    // Calculate gaps between consecutive tests
    const gaps = [];
    for (let i = 1; i < sortedDates.length; i++) {
      const gap = this.calculateTimeGap(
        sortedDates[i - 1].attemptDate,
        sortedDates[i].attemptDate
      );
      if (gap.isValid) {
        gaps.push(gap.days);
      }
    }

    const averageGap = gaps.length > 0 ? gaps.reduce((a, b) => a + b, 0) / gaps.length : null;
    const gapVariance = gaps.length > 1 ? this.calculateVariance(gaps) : 0;
    
    // Determine consistency
    let consistency = 'unknown';
    if (gaps.length >= 2) {
      if (gapVariance < 25) { // Less than 5 days standard deviation
        consistency = 'very_consistent';
      } else if (gapVariance < 100) { // Less than 10 days standard deviation  
        consistency = 'consistent';
      } else {
        consistency = 'inconsistent';
      }
    }

    // Generate pattern insights
    const pattern = this.determineAttemptPattern(gaps, averageGap);
    const recommendations = this.generateAttemptRecommendations(pattern, consistency, averageGap);

    return {
      totalTests: attemptDates.length,
      testsWithDates: sortedDates.length,
      pattern,
      consistency,
      averageGap: averageGap ? Math.round(averageGap) : null,
      gapRange: gaps.length > 0 ? { min: Math.min(...gaps), max: Math.max(...gaps) } : null,
      recommendations,
      timeline: sortedDates.map(date => ({
        testId: date.testId,
        attemptDate: date.attemptDate,
        formattedDate: date.formattedDate
      }))
    };
  }

  /**
   * Generate AI context based on attempt dates
   * @param {string} currentTestId - Current test being analyzed
   * @param {Array} historicalTests - Historical test data with attempt dates
   * @returns {Object} AI context with date-based insights
   */
  generateDateBasedAIContext(currentTestId, historicalTests) {
    if (!historicalTests || historicalTests.length === 0) {
      return {
        timeContext: 'first_test',
        gapAnalysis: null,
        preparation: 'initial'
      };
    }

    // Find current test attempt date
    const currentTest = historicalTests.find(test => test.testId === currentTestId);
    
    if (!currentTest?.attemptDate) {
      return {
        timeContext: 'unknown_date',
        gapAnalysis: null,
        preparation: 'unknown'
      };
    }

    // Sort historical tests by date
    const sortedTests = historicalTests
      .filter(test => test.attemptDate && test.testId !== currentTestId)
      .sort((a, b) => new Date(a.attemptDate) - new Date(b.attemptDate));

    if (sortedTests.length === 0) {
      return {
        timeContext: 'first_test',
        gapAnalysis: null,
        preparation: 'initial'
      };
    }

    // Find the most recent previous test
    const previousTest = sortedTests[sortedTests.length - 1];
    const timeGap = this.calculateTimeGap(previousTest.attemptDate, currentTest.attemptDate);

    // Analyze preparation time
    let preparation = 'standard';
    if (timeGap.isValid) {
      if (timeGap.days < 7) {
        preparation = 'rushed';
      } else if (timeGap.days > 21) {
        preparation = 'extended';
      } else if (timeGap.days >= 14 && timeGap.days <= 21) {
        preparation = 'optimal';
      }
    }

    return {
      timeContext: 'sequential',
      gapAnalysis: {
        previousTest: previousTest.testId,
        daysBetween: timeGap.days,
        weeksBetween: timeGap.weeks,
        isConsistent: timeGap.isConsistent
      },
      preparation,
      totalPreviousTests: sortedTests.length
    };
  }

  /**
   * Helper: Calculate variance of an array
   */
  calculateVariance(array) {
    if (array.length < 2) return 0;
    
    const mean = array.reduce((a, b) => a + b, 0) / array.length;
    const squaredDiffs = array.map(value => Math.pow(value - mean, 2));
    return squaredDiffs.reduce((a, b) => a + b, 0) / array.length;
  }

  /**
   * Helper: Determine attempt pattern type
   */
  determineAttemptPattern(gaps, averageGap) {
    if (!gaps || gaps.length === 0) return 'unknown';
    
    if (averageGap < 10) {
      return 'frequent'; // Tests taken very frequently
    } else if (averageGap <= 14) {
      return 'regular'; // Regular weekly/bi-weekly pattern
    } else if (averageGap <= 21) {
      return 'spaced'; // Well-spaced tests
    } else {
      return 'irregular'; // Long gaps between tests
    }
  }

  /**
   * Helper: Generate recommendations based on attempt pattern
   */
  generateAttemptRecommendations(pattern, consistency, averageGap) {
    const recommendations = [];
    
    if (pattern === 'frequent' && averageGap < 7) {
      recommendations.push('Consider allowing more time between tests for better preparation');
    }
    
    if (pattern === 'irregular' || consistency === 'inconsistent') {
      recommendations.push('Try to maintain a regular testing schedule for better progress tracking');
    }
    
    if (pattern === 'regular' && consistency === 'very_consistent') {
      recommendations.push('Excellent testing rhythm - maintaining consistent preparation time');
    }
    
    if (averageGap > 30) {
      recommendations.push('Long gaps between tests may affect momentum - consider more frequent practice');
    }
    
    return recommendations;
  }

  /**
   * Format date for display
   * @param {string} dateString - Date string (YYYY-MM-DD or ISO format)
   * @returns {string} Formatted date string
   */
  formatDisplayDate(dateString) {
    if (!dateString) return 'Not attempted';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        timeZone: 'Asia/Kolkata'
      });
    } catch (error) {
      console.error('Error formatting display date:', error);
      return 'Invalid date';
    }
  }

  /**
   * Clear cache
   */
  clearCache() {
    this.attemptDateCache.clear();
  }
}

export default new TestAttemptDateService();